import React from "react";
import { Check, IndianRupee } from "lucide-react";

const STATUS_META = {
  planning: { bg: "#B5793F", label: "Planning" },
  in_progress: { bg: "#19B5DE", label: "In progress" },
  on_hold: { bg: "#8A7E6B", label: "On hold" },
  completed: { bg: "#23C795", label: "Completed" },
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function ProjectView({ p }) {
  const st = STATUS_META[p.status] || STATUS_META.planning;
  return (
    <article className="bg-white rounded-2xl p-6 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`my-project-${p.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 text-[#F6F1E5]" style={{ background: st.bg }}>{st.label}</span>
        <span className="rp-display text-lg text-[#262016]">{p.title}</span>
        <span className="text-[11px] text-[#8A7E6B] ml-auto">Started {new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
      </div>

      {p.description && <p className="mt-3 text-sm text-[#5C5042] leading-relaxed whitespace-pre-wrap">{p.description}</p>}

      <div className="mt-5">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B]">Progress</p>
          <p className="text-xs font-semibold text-[#262016]">{p.progress}%</p>
        </div>
        <div className="h-2 rounded-full bg-[#26201614] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: "linear-gradient(90deg,#B5793F,#23C795)" }} />
        </div>
      </div>

      {p.milestones.length > 0 && (
        <div className="mt-5 space-y-3">
          {p.milestones.map((m) => (
            <div key={m.id} className="flex items-center gap-2.5 rounded-xl bg-white/70 border border-[#26201610] px-3 py-2">
              <span className="w-5 h-5 rounded-md border flex items-center justify-center shrink-0"
                    style={{ background: m.done ? "#23C795" : "transparent", borderColor: m.done ? "#23C795" : "#26201640" }}>
                {m.done && <Check size={12} className="text-white" />}
              </span>
              <span className={`flex-1 text-sm ${m.done ? "line-through text-[#8A7E6B]" : "text-[#262016]"}`}>{m.title}</span>
              {m.due && <span className="text-[11px] text-[#8A7E6B]">{m.due}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-5 mt-6">
        {[["Project value", inr(p.price), "#262016"], ["Paid", inr(p.paid), "#23C795"], ["Balance", inr(p.balance), p.balance > 0 ? "#B5793F" : "#23C795"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl bg-white/70 border border-[#26201610] p-3 text-center">
            <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B]">{l}</p>
            <p className="rp-display text-lg mt-0.5" style={{ color: c }}>{v}</p>
          </div>
        ))}
      </div>

      {p.payments.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B] mb-2">Payments</p>
          <div className="space-y-3">
            {p.payments.map((x) => (
              <div key={x.id} className="flex items-center gap-3 rounded-xl bg-white/70 border border-[#26201610] px-3 py-2">
                <IndianRupee size={13} style={{ color: "#23C795" }} />
                <span className="text-sm font-semibold text-[#262016]">{inr(x.amount)}</span>
                <span className="text-xs text-[#8A7E6B]">{x.date} · {x.mode}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function MyProjects({ projects }) {
  return (
    <div data-testid="my-projects">
      <p className="rp-display text-2xl text-[#262016] mb-2">Your projects</p>
      <p className="text-sm text-[#5C5042] mb-6">Live view of progress, milestones and payments.</p>
      <div className="space-y-6">
        {projects.map((p) => <ProjectView key={p.id} p={p} />)}
      </div>
    </div>
  );
}
